import { parseLocalDate } from '@/utils/dates';
import { differenceInCalendarDays } from 'date-fns';

export interface ConflictTask {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  assigned_member_id: number | null;
}

export interface ConflictMember {
  id: number;
  name: string;
}

export interface TaskOverlap {
  movedTask: ConflictTask;
  conflictingTask: ConflictTask;
  teamMember: ConflictMember;
  overlapStart: Date;
  overlapEnd: Date;
  overlapDays: number;
}

function tasksOverlap(a: ConflictTask, b: ConflictTask): boolean {
  const aStart = parseLocalDate(a.start_date);
  const aEnd = parseLocalDate(a.end_date);
  const bStart = parseLocalDate(b.start_date);
  const bEnd = parseLocalDate(b.end_date);
  if (
    isNaN(aStart.getTime()) || isNaN(aEnd.getTime()) ||
    isNaN(bStart.getTime()) || isNaN(bEnd.getTime())
  ) return false;
  return aStart <= bEnd && aEnd >= bStart;
}

/**
 * Detect overlaps between `task` and any other task assigned to the same
 * team member. Tasks without an assigned member never produce conflicts.
 * Pass `excludeIds` to skip tasks whose dates are also being updated (e.g.
 * dependents that are being shifted together with the primary task).
 */
export function detectTaskOverlaps(
  task: ConflictTask,
  allTasks: ConflictTask[],
  teamMembers: ConflictMember[],
  excludeIds: Set<number> = new Set(),
): TaskOverlap[] {
  if (!task.assigned_member_id) return [];

  const teamMember = teamMembers.find((tm) => tm.id === task.assigned_member_id);
  if (!teamMember) return [];

  const conflicts: TaskOverlap[] = [];

  for (const other of allTasks) {
    if (other.id === task.id) continue;
    if (excludeIds.has(other.id)) continue;
    if (other.assigned_member_id !== task.assigned_member_id) continue;

    if (tasksOverlap(task, other)) {
      const taskStart = parseLocalDate(task.start_date);
      const taskEnd = parseLocalDate(task.end_date);
      const otherStart = parseLocalDate(other.start_date);
      const otherEnd = parseLocalDate(other.end_date);

      const overlapStart = taskStart > otherStart ? taskStart : otherStart;
      const overlapEnd = taskEnd < otherEnd ? taskEnd : otherEnd;
      const overlapDays = differenceInCalendarDays(overlapEnd, overlapStart) + 1;

      conflicts.push({
        movedTask: task,
        conflictingTask: other,
        teamMember,
        overlapStart,
        overlapEnd,
        overlapDays,
      });
    }
  }

  return conflicts;
}

/**
 * Aggregate conflicts across a set of tasks that are all being moved together
 * (primary drag + shifted dependents). Each task is checked against all tasks
 * not in the moved set, so intra-group overlaps are ignored.
 */
export function detectAllConflicts(
  movedTasks: ConflictTask[],
  allTasks: ConflictTask[],
  teamMembers: ConflictMember[],
): TaskOverlap[] {
  const movedIds = new Set(movedTasks.map((t) => t.id));
  const conflicts: TaskOverlap[] = [];

  for (const moved of movedTasks) {
    const taskConflicts = detectTaskOverlaps(moved, allTasks, teamMembers, movedIds);
    conflicts.push(...taskConflicts);
  }

  return conflicts;
}
